import { useState, useEffect, useCallback, useRef } from 'react';
import ScaffoldPanel from '../ERDEditor/ScaffoldPanel';
import {
  MdQuestionAnswer,
  MdEdit,
  MdDelete,
  MdVisibility,
  MdVisibilityOff,
  MdAdd,
  MdMoreVert,
  MdSave,
  MdCancel,
  MdExpandMore,
  MdExpandLess,
  MdClose,
  MdAutoAwesome
} from 'react-icons/md';
import ReactMarkdown from 'react-markdown'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import 'katex/dist/katex.min.css'
import CodeSnippet from './CodeSnippet';
import { QuestionCard } from '../ERDEditor/PedagogicalAgent';
import { aiGuidelineApi } from '../../utils/api/DocumentEditorApi/AIGuidelineApi';
import { activityApi } from '../../utils/api/DocumentEditorApi/ActivityApi';
import { getAuthHeaders } from '../../utils/api/ApiUtils';

const QAPanel = ({
  documentContent,
  documentTitle,
  currentDocument,
  userRole,
  qaApi,
  getClassContext,
  generatedQuestions = [],
  setGeneratedQuestions,
  isStudent = false,
  isPublicView = false,
  selectedContext = '', // Text selected from preview pane
  erdState = null, // ERD state from DocumentEditor (when document contains ERD)
  apiKey = null, // OpenAI API key for ERD feedback
  username = null, // Username for ERD feedback
  activePanel = 'qa',        // 'qa' | 'ai-guide' — controlled by parent (index.jsx)
  onPanelChange = null,      // (panel: string) => void
  erdContext = null,         // context passed from ERDSnippet when "Get AI Feedback" clicked
  feedbackRequestCount = 0,  // increments each time "Get AI Feedback" is clicked
  feedbackEnvRef = null,     // ref holding the environment ID from the ERD snippet that triggered feedback
  feedbackReqsRef = null,    // ref holding the inline requirements from the document block
  sectionId = null,          // section_id of the current document, used to fetch the AI guideline
  sectionName = null         // human-readable section name shown as module context in AI Guide
}) => {
  // All React hooks must be called at the top level, before any conditional returns
  const [envQuota, setEnvQuota] = useState(null);
  const [quotaEnvId, setQuotaEnvId] = useState(null); // env currently being tracked for quota
  const fetchQuota = useCallback((envId) => {
    const classId = getClassContext?.()?.class_id;
    if (!classId || !envId || envId === 'None') return;
    fetch(`/api/quota/environment?class_id=${encodeURIComponent(classId)}&environment_id=${encodeURIComponent(envId)}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.success) setEnvQuota(data); })
      .catch(() => {});
  }, [getClassContext]);

  // Capture env and fetch quota whenever feedback is triggered
  useEffect(() => {
    if (!feedbackRequestCount) return;
    const envId = feedbackEnvRef?.current;
    if (envId) { setQuotaEnvId(envId); fetchQuota(envId); }
  }, [feedbackRequestCount, fetchQuota]);

  // Re-fetch after feedback completes (feedbackId changes)
  useEffect(() => {
    if (erdState?.feedbackId && quotaEnvId) fetchQuota(quotaEnvId);
  }, [erdState?.feedbackId, quotaEnvId, fetchQuota]);

  const [qas, setQas] = useState([]);
  const [additionalQas, setAdditionalQas] = useState([]);
  const [tempStudentQAs, setTempStudentQAs] = useState([]); // Temporary Q&As for students to see their own questions
  const [loading, setLoading] = useState(true);
  const [additionalLoading, setAdditionalLoading] = useState(false);
  const [newQuestion, setNewQuestion] = useState('');
  const [contextText, setContextText] = useState(''); // Selected text from preview
  const [isCreatingQuestion, setIsCreatingQuestion] = useState(false);
  const [expandedQA, setExpandedQA] = useState(null);
  const [expandedAdditionalQA, setExpandedAdditionalQA] = useState(null);
  const [editingQA, setEditingQA] = useState(null);
  const [editedQuestion, setEditedQuestion] = useState('');
  const [editedAnswer, setEditedAnswer] = useState('');
  const [deleteConfirmQA, setDeleteConfirmQA] = useState(null);
  const [showActionsFor, setShowActionsFor] = useState(null);
  const [activeQATab, setActiveQATab] = useState('document'); // 'document', 'additional', or 'erd'

  // AI Guide tab state
  const [aiGuideContent, setAiGuideContent] = useState(null); // { markdownText, question }
  const [aiGuideLoading, setAiGuideLoading] = useState(false);

  // MCQ answer tracking and scaffold stage state
  const [mcqAnswers, setMcqAnswers] = useState({}); // { question_id: { question_text, selected_options, selected_text } }
  const [currentStageIndex, setCurrentStageIndex] = useState(0); // 0 = stage 1 (MCQs), advance on each "next" click
  const [stageContent, setStageContent] = useState(null); // { type, followup_text?, qa_pairs? }
  const [stageError, setStageError] = useState(null); // error message from followup API
  const [isGeneratingFollowUp, setIsGeneratingFollowUp] = useState(false);
  // Keep legacy alias so any remaining references don't break
  const followUpText = stageContent?.type !== 'question_answer' ? (stageContent?.followup_text || null) : null;
  const feedbackIdRef = useRef(null); // stores feedbackId from erdState

  // Build inline context for followup requests so backend doesn't need DB lookup
  const buildFollowupContext = () => ({
    relevant_statements: erdState?.relevantStatements || [],
    feedback_text: (erdState?.feedbackItems || []).filter(i => i.type === 'text').map(i => i.content).join('\n'),
    scaffold_stages: erdState?.scaffoldStages || [],
    erd_data: erdState?.erdData || null,
    environment: erdState?.selectedEnvironment || '',
    pedagogical_items: (erdState?.feedbackItems || []).filter(i => i.type === 'mcq' || i.type === 'short_answer'),
  });
  const guideContentCache = useRef({}); // { [sectionId ?? 'root']: aiGuideContent }
  const prevSectionIdRef = useRef(sectionId);

  // AI Guide MCQ history for the current section (own for students, all for educators)
  const [aiGuideHistory, setAiGuideHistory] = useState([]); // array of activity records
  const lastFetchedHistorySectionRef = useRef(undefined);

  // AI Guideline state (fetched from backend for the current section)
  const [guideline, setGuideline] = useState(null); // { title, content } or null
  const [guidelineLoading, setGuidelineLoading] = useState(false);
  const lastFetchedSectionRef = useRef(undefined); // track which sectionId we last fetched

  // Resolved effective module — the section whose policy is actually used (may be an ancestor)
  const [effectiveModule, setEffectiveModule] = useState(null); // { section_id, section_name, policy_name, is_inherited }

  // Save/restore AI Guide content when switching between sections
  useEffect(() => {
    if (sectionId === prevSectionIdRef.current) return;
    // Save current content under old section key
    guideContentCache.current[prevSectionIdRef.current ?? 'root'] = aiGuideContent;
    // Restore content for new section (null if never visited)
    const cached = guideContentCache.current[sectionId ?? 'root'] ?? null;
    setAiGuideContent(cached);
    prevSectionIdRef.current = sectionId;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionId]);

  // Keep cache in sync whenever content is set
  useEffect(() => {
    if (aiGuideContent) {
      guideContentCache.current[sectionId ?? 'root'] = aiGuideContent;
    }
  }, [aiGuideContent, sectionId]);

  // Track feedbackId from erdState so follow-up can reference the session
  useEffect(() => {
    if (erdState?.feedbackId) {
      feedbackIdRef.current = erdState.feedbackId;
    }
  }, [erdState?.feedbackId]);

  // Load AI Guide content when "Get AI Feedback" is explicitly triggered (feedbackRequestCount increments).
  // Skip count=0 (initial mount). Uses cached content if available for this section.
  const prevFeedbackCountRef = useRef(0);
  useEffect(() => {
    if (feedbackRequestCount === 0) return;
    if (feedbackRequestCount === prevFeedbackCountRef.current) return;
    prevFeedbackCountRef.current = feedbackRequestCount;

    // Trigger ERD feedback — pass sectionId so backend can fetch the guideline itself
    const envId = feedbackEnvRef?.current || undefined;
    const reqs = feedbackReqsRef?.current || undefined;
    console.log('[QAPanel] feedbackRequestCount fired, envId:', envId, 'reqs:', reqs ? '(present)' : '(none)', 'sectionId:', sectionId);
    console.log('[QAPanel] erdState:', erdState, 'getFeedback:', typeof erdState?.getFeedback);
    // Reset follow-up state for fresh feedback
    setMcqAnswers({});
    setStageContent(null);
    setStageError(null);
    setCurrentStageIndex(0);
    feedbackIdRef.current = null;
    if (erdState?.getFeedback) {
      erdState.getFeedback(envId, reqs, sectionId);
    } else {
      console.warn('[QAPanel] erdState.getFeedback is not available — request not sent');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbackRequestCount]);

  // Auto-trigger stage 0 when feedback arrives and stage 0 is not 'mcq'
  const prevFeedbackIdRef = useRef(null);
  useEffect(() => {
    const fid = erdState?.feedbackId;
    const s0 = erdState?.stage0Type || 'mcq';
    if (!fid || fid === prevFeedbackIdRef.current) return;
    if (s0 === 'mcq') return; // MCQs shown natively from feedbackItems
    prevFeedbackIdRef.current = fid;

    const stages = erdState?.scaffoldStages || [];
    const stage0 = stages[0];
    if (!stage0) return;

    // New policy engine (ScaffoldPanel) has stage_id field — it handles its own triggering
    if (stage0.stage_id) return;

    // Auto-fetch stage 0 if TRIGGER: always (old ## Scaffolding Rules format only)
    if ((stage0.trigger || '').toLowerCase() === 'always') {
      const classContext = getClassContext ? getClassContext() : null;
      const classId = classContext?.class_id;
      if (!classId) return;
      setIsGeneratingFollowUp(true);
      fetch('/api/erd-followup', {
        method: 'POST',
        headers: getAuthHeaders(classContext),
        body: JSON.stringify({
          feedback_id: fid,
          class_id: classId,
          stage_index: 0,
          answers: [],
          ...buildFollowupContext()
        })
      })
        .then(r => r.json())
        .then(data => {
          if (data.success) { setStageContent(data); setCurrentStageIndex(0); }
          else console.warn('[QAPanel] auto stage0 error:', data.error);
        })
        .catch(err => console.warn('[QAPanel] auto stage0 fetch error:', err))
        .finally(() => setIsGeneratingFollowUp(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [erdState?.feedbackId, erdState?.stage0Type]);

  // Fetch AI Guide MCQ history whenever sectionId changes
  useEffect(() => {
    if (sectionId === lastFetchedHistorySectionRef.current) return;
    lastFetchedHistorySectionRef.current = sectionId;
    const fetchHistory = async () => {
      try {
        const classContext = getClassContext ? getClassContext() : null;
        const res = await activityApi.getAiGuideHistory(sectionId, { limit: 100 }, classContext);
        setAiGuideHistory(res?.history || []);
      } catch (err) {
        console.warn('[QAPanel] Failed to fetch AI guide history:', err);
        setAiGuideHistory([]);
      }
    };
    fetchHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionId]);

  // Fetch AI Guideline for the current section whenever sectionId changes
  useEffect(() => {
    if (isStudent) return; // students never see guidelines
    if (sectionId === lastFetchedSectionRef.current) return; // already fetched for this section
    lastFetchedSectionRef.current = sectionId;

    const fetchGuideline = async () => {
      setGuidelineLoading(true);
      try {
        const classContext = getClassContext ? getClassContext() : null;
        const res = await aiGuidelineApi.getGuideline(sectionId, classContext);
        setGuideline(res?.guideline || null);
      } catch (err) {
        console.warn('[QAPanel] Failed to fetch AI guideline:', err);
        setGuideline(null);
      } finally {
        setGuidelineLoading(false);
      }
    };

    fetchGuideline();
  }, [sectionId, isStudent, getClassContext]);

  // Fetch effective module name (resolves ancestor chain) whenever sectionId changes.
  // Always re-fetches so policy name changes are reflected immediately.
  useEffect(() => {
    if (!sectionId) { setEffectiveModule(null); return; }
    setEffectiveModule(null); // clear stale data while fetching
    const classContext = getClassContext ? getClassContext() : null;
    aiGuidelineApi.getEffectiveModule(sectionId, classContext)
      .then(data => { if (data?.success) setEffectiveModule(data); })
      .catch(() => {});
  }, [sectionId, getClassContext]);

  // Safety check for documentTitle - must be done before hooks that depend on it
  const safeDocumentTitle = documentTitle || 'untitled_document';
  const documentId = `doc_${safeDocumentTitle.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase()}`;
  const isInstructor = userRole && ['Instructor', 'TA', 'Admin'].includes(userRole);

  // Check if document contains ERD components
  const hasERD = documentContent && (documentContent.includes('```erd') || documentContent.includes('```erd:editor'));

  const loadQAs = useCallback(async () => {
    if (!qaApi || !getClassContext || !currentDocument || !currentDocument.id) {
      console.log('QA Panel - loadQAs: Missing requirements', {
        qaApi: !!qaApi,
        getClassContext: !!getClassContext,
        currentDocument: !!currentDocument,
        currentDocumentId: currentDocument?.id
      });
      setLoading(false);
      return;
    }
    
    try {
      console.log('QA Panel - Loading QAs for document:', currentDocument.id);
      setLoading(true);
      const data = await qaApi.getQAList(currentDocument.id, getClassContext());
      console.log('QA Panel - QAs loaded:', data);
      if (data.success) {
        setQas(data.qa_entries || []);
      }
    } catch (error) {
      console.error('Failed to load Q&As:', error);
    } finally {
      setLoading(false);
    }
  }, [currentDocument, qaApi, getClassContext]);

  const loadAdditionalQAs = useCallback(async () => {
    if (!qaApi || !getClassContext || !currentDocument || !currentDocument.id) {
      console.log('QA Panel - loadAdditionalQAs: Missing requirements', {
        qaApi: !!qaApi,
        getClassContext: !!getClassContext,
        currentDocument: !!currentDocument,
        currentDocumentId: currentDocument?.id
      });
      return;
    }
    
    try {
      console.log('QA Panel - Loading Additional QAs for document:', currentDocument.id);
      setAdditionalLoading(true);
      const data = await qaApi.getAdditionalQAList(currentDocument.id, getClassContext());
      console.log('QA Panel - Additional QAs loaded:', data);
      if (data.success) {
        setAdditionalQas(data.additional_qa_entries || []);
      }
    } catch (error) {
      console.error('Failed to load additional Q&As:', error);
    } finally {
      setAdditionalLoading(false);
    }
  }, [currentDocument, qaApi, getClassContext]);

  // Load Q&As when component mounts or when currentDocument changes
  useEffect(() => {
    console.log('QA Panel - useEffect triggered. Current document:', currentDocument);
    loadQAs();
    loadAdditionalQAs();
    // Clear temporary student Q&As when document changes
    setTempStudentQAs([]);
  }, [loadQAs, loadAdditionalQAs, currentDocument]);

  // Update context text when selectedContext prop changes
  useEffect(() => {
    if (selectedContext) {
      setContextText(selectedContext);
    }
  }, [selectedContext]);

  // Early return with error state if critical props are missing
  if (!qaApi || !getClassContext) {
    return (
      <div className="w-full bg-white flex flex-col h-full">
        <div className="h-16 bg-white border-b border-gray-200/60 flex items-center justify-center px-6">
          <div className="text-red-600">Error: Missing required props</div>
        </div>
      </div>
    );
  }


  // Handle adding generated questions to Q&A panel (DEPRECATED - questions are now auto-saved)
  // eslint-disable-next-line no-unused-vars
  const handleAddGeneratedQuestion = async (generatedQuestion) => {
    // This function is no longer used since generated questions are automatically saved to database
    console.log('handleAddGeneratedQuestion called but is deprecated - questions should already be in database');
    
    // Refresh Q&A list instead to show newly saved questions
    try {
      await loadQAs();
      await loadAdditionalQAs();
    } catch (error) {
      console.error('Failed to refresh Q&A lists:', error);
    }
  };

  const handleCreateQuestion = async () => {
    if (!newQuestion.trim()) return;

    // Check if we have document content to work with
    if (!documentContent || documentContent.trim().length === 0) {
      alert('Please add some content to your document before asking questions.');
      return;
    }

    // Check if we have a document context (either saved or being edited)
    if (!currentDocument && !documentTitle.trim()) {
      alert('Please save your document or give it a title before asking questions.');
      return;
    }

    try {
      setIsCreatingQuestion(true);
      
      // Determine if this should be an additional question (for non-instructors or when explicitly requested)
      const isAdditional = !isInstructor || activeQATab === 'additional';
      
      // Use document ID if available, otherwise use title as identifier
      const documentIdToUse = currentDocument?.id || documentTitle || `unsaved-${Date.now()}`;
      
      // Use current editor content (not saved content) to capture latest changes
      const data = await qaApi.createQA(
        documentIdToUse,
        newQuestion,
        documentContent, // This is the current content in the editor
        '', // document summary - could be generated if needed
        isAdditional,
        getClassContext(),
        currentDocument?.environment_id || null
      );

      if (data.success) {
        if (isAdditional) {
          setAdditionalQas(prev => [...prev, data.qa]);
          // If this is a student question, also add it to temporary display
          if (isStudent) {
            setTempStudentQAs(prev => [...prev, data.qa]);
          }
        } else {
          setQas(prev => [...prev, data.qa]);
        }
        setNewQuestion('');
      } else {
        throw new Error(data.error || 'Failed to create question');
      }
    } catch (error) {
      console.error('Failed to create question:', error);
      alert(`Error creating question: ${error.message}`);
    } finally {
      setIsCreatingQuestion(false);
    }
  };

  const handlePromoteQuestion = async (qaId) => {
    try {
      const data = await qaApi.promoteAdditionalQA(qaId, getClassContext());
      
      if (data.success) {
        // Remove from additional questions
        setAdditionalQas(prev => prev.filter(qa => qa.qa_id !== qaId));
        // Remove from temporary student questions if it exists there
        setTempStudentQAs(prev => prev.filter(qa => qa.qa_id !== qaId));
        // Add to document questions
        setQas(prev => [...prev, data.promoted_qa]);
        alert('Question promoted successfully!');
      } else {
        throw new Error(data.error || 'Failed to promote question');
      }
    } catch (error) {
      console.error('Failed to promote question:', error);
      alert(`Error promoting question: ${error.message}`);
    }
  };

  const handleEditQA = (qa) => {
    setEditingQA(qa.qa_id);
    setEditedQuestion(qa.rephrased_question);
    setEditedAnswer(qa.answer);
    setShowActionsFor(null);
  };

  const handleSaveEdit = async (qaId) => {
    try {
      const data = await qaApi.updateQA(
        qaId,
        editedQuestion,
        editedAnswer,
        true,
        getClassContext()
      );

      if (data.success) {
        setQas(prev => prev.map(qa => 
          qa.qa_id === qaId 
            ? { ...qa, rephrased_question: editedQuestion, answer: editedAnswer }
            : qa
        ));
        setEditingQA(null);
      } else {
        throw new Error(data.error || 'Failed to update Q&A');
      }
    } catch (error) {
      console.error('Failed to update Q&A:', error);
      alert(`Error updating Q&A: ${error.message}`);
    }
  };

  const handleCancelEdit = () => {
    setEditingQA(null);
    setEditedQuestion('');
    setEditedAnswer('');
  };

  const handleToggleVisibility = async (qaId, currentVisible) => {
    try {
      const data = await qaApi.toggleQAVisibility(qaId, !currentVisible, getClassContext());
      
      if (data.success) {
        setQas(prev => prev.map(qa => 
          qa.qa_id === qaId ? { ...qa, visible: !currentVisible } : qa
        ));
        setShowActionsFor(null);
      } else {
        throw new Error(data.error || 'Failed to toggle visibility');
      }
    } catch (error) {
      console.error('Failed to toggle visibility:', error);
      alert(`Error toggling visibility: ${error.message}`);
    }
  };

  const handleDeleteQA = async (qaId) => {
    try {
      const data = await qaApi.deleteQA(qaId, getClassContext());
      
      if (data.success) {
        setQas(prev => prev.filter(qa => qa.qa_id !== qaId));
        setDeleteConfirmQA(null);
      } else {
        throw new Error(data.error || 'Failed to delete Q&A');
      }
    } catch (error) {
      console.error('Failed to delete Q&A:', error);
      alert(`Error deleting Q&A: ${error.message}`);
    }
  };

  const handleDeleteClick = (qaId) => {
    setDeleteConfirmQA(qaId);
    setShowActionsFor(null);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmQA(null);
  };

  const toggleExpanded = (qaId) => {
    setExpandedQA(expandedQA === qaId ? null : qaId);
  };

  const toggleExpandedAdditional = (qaId) => {
    setExpandedAdditionalQA(expandedAdditionalQA === qaId ? null : qaId);
  };

  if (loading) {
    return (
      <div className="w-full bg-white flex flex-col h-full">
        <div className="h-16 bg-white border-b border-gray-200/60 flex items-center justify-center px-6">
          <div className="animate-spin w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  // Submit handler for the QuestionCard in AI Guide tab — grades, logs activity, refreshes history
  const handleAIGuideSubmitAnswer = async (questionId, selectedOptions, questionData) => {
    // questionData is the full question object passed by QuestionCard (rubric, explanation, lo, etc.)
    console.log('[handleAIGuideSubmitAnswer] questionId:', questionId, 'questionData:', questionData);
    const correctAnswer = questionData?.rubric || [];
    const isCorrect = JSON.stringify([...selectedOptions].sort()) ===
      JSON.stringify([...correctAnswer].sort());

    const result = {
      is_correct: isCorrect,
      correct_answer: correctAnswer,
      explanation: questionData?.explanation || ''
    };

    // Record this answer for follow-up text generation
    const selectedText = (questionData?.options || [])
      .filter(o => selectedOptions.includes(o.key))
      .map(o => `${o.key}: ${o.text}`)
      .join(', ');

    const updatedAnswers = {
      ...mcqAnswers,
      [questionId]: {
        question_text: questionData?.question || '',
        selected_options: selectedOptions,
        selected_text: selectedText
      }
    };
    setMcqAnswers(updatedAnswers);

    // Log progress
    const totalMcqs = (erdState?.feedbackItems || []).filter(i => i.type === 'mcq').length;
    const answeredCount = Object.keys(updatedAnswers).length;
    console.log(`[QAPanel] MCQ answered: ${answeredCount}/${totalMcqs}`);

    // Log the attempt as an activity (fire-and-forget; don't block the UI)
    try {
      const classContext = getClassContext ? getClassContext() : null;
      const documentId = currentDocument?.document_id || currentDocument?.id || 'ai-guide';
      await activityApi.logActivity(
        documentId,
        'ai_guide_mcq',
        `AI Guide MCQ: ${questionId} — ${isCorrect ? 'correct' : 'incorrect'}`,
        {
          score: isCorrect ? 1.0 : 0.0,
          metadata: {
            question_id: questionId,
            lo: questionData?.lo || null,
            section_id: sectionId,
            section_name: sectionName,
            selected: selectedOptions,
            correct_answer: correctAnswer,
            is_correct: isCorrect
          }
        },
        classContext
      );

      // Refresh history so it stays up to date for educator view and AI context
      const histRes = await activityApi.getAiGuideHistory(sectionId, { limit: 100 }, classContext);
      setAiGuideHistory(histRes?.history || []);
    } catch (err) {
      console.warn('[QAPanel] Failed to log AI guide MCQ activity:', err);
    }

    return result;
  };

  return (
    <div className="w-full bg-white flex flex-col h-full">
      {/* Top-level tab bar: AI Guide only (Q&A tab hidden) */}
      <div className="flex border-b-2 border-gray-200 flex-shrink-0">
        <button
          onClick={() => onPanelChange && onPanelChange('ai-guide')}
          className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 text-sm font-medium transition-colors duration-200 ${
            activePanel === 'ai-guide'
              ? 'text-indigo-900 border-b-2 border-indigo-600 bg-white'
              : 'text-indigo-600 hover:text-indigo-900 bg-indigo-50/20'
          }`}
        >
          <MdAutoAwesome size={16} />
          <span>AI Guide</span>
        </button>
      </div>

      {/* AI Guide Panel */}
      {activePanel === 'ai-guide' && (
        <div className="flex-1 overflow-y-auto min-h-0 flex flex-col">
          {/* Module header — always visible when a section is active */}
          <div className="flex-shrink-0 px-6 pt-4 pb-3 border-b border-indigo-100 bg-indigo-50/40">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <MdAutoAwesome size={15} className="text-indigo-500 flex-shrink-0" />
                <span className="text-xs font-semibold text-indigo-800 uppercase tracking-wide">
                  {guidelineLoading
                    ? 'Loading module…'
                    : effectiveModule?.policy_name
                    || effectiveModule?.section_name
                    || sectionName
                    || guideline?.content?.match(/^#\s+(.+)/m)?.[1]
                    || guideline?.title
                    || (sectionId ? '…' : 'No module selected')}
                </span>
              </div>
              {guidelineLoading && (
                <div className="w-3 h-3 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
              )}
            </div>
            {!sectionId && !guidelineLoading && (
              <p className="text-xs text-indigo-400 mt-1">Open a document inside a section to activate its AI Guide.</p>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {/* Quota badge — first line */}
            {!isPublicView && envQuota && (
              <div className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-semibold ${
                envQuota.limit === -1
                  ? 'bg-gray-50 border-gray-200 text-gray-600'
                  : envQuota.remaining <= 0
                  ? 'bg-red-50 border-red-200 text-red-700'
                  : envQuota.remaining <= 2
                  ? 'bg-amber-50 border-amber-200 text-amber-700'
                  : 'bg-green-50 border-green-200 text-green-700'
              }`}>
                {envQuota.limit === -1
                  ? '∞ feedback quota'
                  : `${envQuota.remaining} of ${envQuota.limit} feedback requests remaining`}
              </div>
            )}

            {/* Guideline content for educator (collapsed summary) — shown when guideline exists */}
            {!isStudent && guideline?.content && (
              <details className="bg-indigo-50 border border-indigo-100 rounded-lg text-sm">
                <summary className="px-4 py-2 cursor-pointer font-medium text-indigo-800 hover:bg-indigo-100 rounded-lg">
                  📋 AI Guideline for this module
                </summary>
                <div className="px-4 py-3 prose prose-sm max-w-none text-indigo-900">
                  <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                    {guideline.content}
                  </ReactMarkdown>
                </div>
              </details>
            )}

            {erdState?.isGettingFeedback ? (
              <div className="flex items-center space-x-3 text-gray-500">
                <div className="animate-spin w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full"></div>
                <span className="text-sm">Getting feedback…</span>
              </div>
            ) : erdState?.feedbackItems?.length > 0 ? (
              <div className="space-y-3">
                {/* Relevant MCQ questions — only shown when scaffold stage 0 is 'mcq' */}
                {(erdState?.stage0Type === 'mcq' || !erdState?.scaffoldStages?.length) &&
                 erdState.feedbackItems.some(item => item.type === 'mcq') && (
                  <div className="mt-4 pt-4 border-t border-indigo-100">
                    <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide mb-3">
                      Related Practice Questions
                    </p>
                    {!stageContent && !isGeneratingFollowUp && (
                      <div className="space-y-3">
                        {erdState.feedbackItems
                          .filter(item => item.type === 'mcq')
                          .map((item, index) => {
                            console.log('[QAPanel] MCQ item:', item);
                            return (
                              <QuestionCard
                                key={item.question_id || index}
                                question={item}
                                onSubmitAnswer={handleAIGuideSubmitAnswer}
                              />
                            );
                          })}
                      </div>
                    )}

                    {/* Scaffold: "next step" trigger — shown when no stage content is active */}
                    {(() => {
                      const stages = erdState?.scaffoldStages || [];
                      const nextIndex = currentStageIndex + 1;
                      const hasNextStage = stages.length === 0 ? currentStageIndex < 2 : nextIndex < stages.length;
                      if (!stageContent && !isGeneratingFollowUp && hasNextStage) {
                        return (
                          <button
                            onClick={async () => {
                              const fid = erdState?.feedbackId;
                              const classContext = getClassContext ? getClassContext() : null;
                              const classId = classContext?.class_id;
                              if (!fid || !classId) {
                                console.warn('[QAPanel] Cannot trigger follow-up: fid=', fid, 'classId=', classId);
                                return;
                              }
                              const targetIndex = nextIndex;
                              setIsGeneratingFollowUp(true);
                              try {
                                const res = await fetch('/api/erd-followup', {
                                  method: 'POST',
                                  headers: getAuthHeaders(classContext),
                                  body: JSON.stringify({
                                    feedback_id: fid,
                                    class_id: classId,
                                    stage_index: targetIndex,
                                    answers: Object.entries(mcqAnswers).map(([qid, a]) => ({ question_id: qid, ...a })),
                                    ...buildFollowupContext()
                                  })
                                });
                                const data = await res.json();
                                if (data.success) {
                                  setStageContent(data);
                                  setStageError(null);
                                  setCurrentStageIndex(targetIndex);
                                } else {
                                  console.warn('[QAPanel] follow-up error:', data.error);
                                  setStageError(data.error || 'Failed to load next step');
                                }
                              } catch (err) {
                                console.warn('[QAPanel] follow-up fetch error:', err);
                                setStageError('Network error loading next step');
                              } finally {
                                setIsGeneratingFollowUp(false);
                              }
                            }}
                            className="mt-4 text-xs text-indigo-500 hover:text-indigo-700 underline text-left"
                          >
                            I have answered all of the questions and still I don't understand what may be wrong with my ERD
                          </button>
                        );
                      }
                      return null;
                    })()}

                    {/* Generating spinner */}
                    {isGeneratingFollowUp && (
                      <div className="mt-4 flex items-center space-x-2 text-indigo-500">
                        <div className="animate-spin w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full" />
                        <span className="text-sm">Generating next step…</span>
                      </div>
                    )}

                    {/* Stage error */}
                    {stageError && !isGeneratingFollowUp && !stageContent && (
                      <div className="mt-3 text-xs text-red-500">
                        ⚠ {stageError} — please try again.
                      </div>
                    )}

                    {/* Stage content rendering */}
                    {stageContent && !isGeneratingFollowUp && (() => {
                      const stages = erdState?.scaffoldStages || [];
                      const nextIndex = currentStageIndex + 1;
                      const hasNextStage = stages.length === 0 ? currentStageIndex < 2 : nextIndex < stages.length;

                      const actionButtons = (
                        <div className="mt-4 flex flex-wrap gap-2">
                          <button
                            onClick={() => onPanelChange && onPanelChange('qa')}
                            className="px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
                          >
                            I want to edit my ERD
                          </button>
                          <button
                            onClick={() => { setStageContent(null); setStageError(null); setCurrentStageIndex(0); }}
                            className="px-3 py-1.5 text-xs font-medium border border-indigo-300 text-indigo-700 rounded hover:bg-indigo-100 transition-colors"
                          >
                            Show me the questions again
                          </button>
                          {hasNextStage && (
                            <button
                              onClick={async () => {
                                const fid = erdState?.feedbackId;
                                const classContext = getClassContext ? getClassContext() : null;
                                const classId = classContext?.class_id;
                                if (!fid || !classId) return;
                                const targetIndex = nextIndex;
                                setStageContent(null);
                                setIsGeneratingFollowUp(true);
                                try {
                                  const res = await fetch('/api/erd-followup', {
                                    method: 'POST',
                                    headers: getAuthHeaders(classContext),
                                    body: JSON.stringify({
                                      feedback_id: fid,
                                      class_id: classId,
                                      stage_index: targetIndex,
                                      answers: Object.entries(mcqAnswers).map(([qid, a]) => ({ question_id: qid, ...a })),
                                      ...buildFollowupContext()
                                    })
                                  });
                                  const data = await res.json();
                                  if (data.success) {
                                    setStageContent(data);
                                    setCurrentStageIndex(targetIndex);
                                  } else {
                                    console.warn('[QAPanel] follow-up error:', data.error);
                                  }
                                } catch (err) {
                                  console.warn('[QAPanel] follow-up fetch error:', err);
                                } finally {
                                  setIsGeneratingFollowUp(false);
                                }
                              }}
                              className="px-3 py-1.5 text-xs font-medium border border-orange-300 text-orange-700 rounded hover:bg-orange-50 transition-colors"
                            >
                              I still need more help
                            </button>
                          )}
                        </div>
                      );

                      if (stageContent.type === 'mcq') {
                        // MCQ items returned from a scaffold mcq stage
                        return (
                          <div className="mt-4">
                            <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide mb-3">
                              Related Practice Questions
                            </p>
                            <div className="space-y-3">
                              {(stageContent.items || []).map((item, idx) => (
                                <QuestionCard
                                  key={item.question_id || idx}
                                  question={item}
                                  onSubmitAnswer={(qid, selectedOptions, questionData) =>
                                    handleAIGuideSubmitAnswer(qid, selectedOptions, questionData)
                                  }
                                />
                              ))}
                            </div>
                            {actionButtons}
                          </div>
                        );
                      }

                      if (stageContent.type === 'question_answer') {
                        // Collapsed Q&A pairs
                        return (
                          <div className="mt-4">
                            <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">
                              📝 Practice Questions
                            </p>
                            {(stageContent.qa_pairs || []).map((qa, i) => (
                              <details key={i} className="mb-2 border border-blue-200 rounded-lg overflow-hidden">
                                <summary className="cursor-pointer px-4 py-3 bg-blue-50 text-sm font-medium text-blue-900 hover:bg-blue-100">
                                  {qa.question}
                                </summary>
                                <div className="px-4 py-3 text-sm text-gray-800 bg-white border-t border-blue-100">
                                  {qa.answer}
                                </div>
                              </details>
                            ))}
                            {actionButtons}
                          </div>
                        );
                      }

                      // concept_explanation or targeted_hint — text box
                      const label = stageContent.type === 'targeted_hint' ? '🎯 Targeted Hint' : '💡 Concept Insight';
                      return (
                        <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                          <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide mb-2">
                            {label}
                          </p>
                          <p className="text-sm text-indigo-900 leading-relaxed">{stageContent.followup_text}</p>
                          {actionButtons}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Policy-driven scaffold engine — condition stage decides whether to scaffold */}
                {erdState?.feedbackId &&
                 erdState?.stage0Type &&
                 erdState.stage0Type !== 'mcq' &&
                 (erdState?.scaffoldStages?.length > 0) && (
                  <div className="mt-4 pt-4 border-t border-indigo-100">
                    <ScaffoldPanel
                      erdState={erdState}
                      sectionId={sectionId}
                      environmentId={quotaEnvId || erdState?.selectedEnvironment}
                      getClassContext={getClassContext}
                      initialStageId={erdState.scaffoldStages[0]?.stage_id || 'stage_1'}
                      onStageComplete={() => { if (quotaEnvId) fetchQuota(quotaEnvId); }}
                      onRevise={() => {
                        // Scroll user back to the ERD editor to revise
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <MdAutoAwesome size={32} className="mx-auto mb-3 text-indigo-300" />
                <p className="text-sm">Click <strong>Get AI Feedback</strong> in an ERD diagram to start.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Q&A Panel (hidden when AI Guide is active) */}
      {activePanel === 'qa' && (<>
      {/* Header */}
      <div className="h-16 bg-white border-b border-gray-200/60 flex items-center justify-between px-6 gap-2 flex-shrink-0">
        <div className="flex items-center space-x-3">
          <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
            <MdQuestionAnswer className="text-white" size={16} />
          </div>
          <div>
            <h2 className="text-lg font-medium text-gray-900">Q&A Panel</h2>
          </div>
        </div>
        <div className="text-xs text-gray-500">
          {isStudent ? (qas.length + tempStudentQAs.length) : qas.length} question{(isStudent ? (qas.length + tempStudentQAs.length) : qas.length) !== 1 ? 's' : ''}
        </div>
      </div>


      {/* Add Question Section - Available for all users */}
      <div className="px-6 py-4 border-b border-gray-200/60 bg-gray-50/30">
        <div className="space-y-3">
          {/* Show selected context if present */}
          {contextText && (
            <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="text-xs font-medium text-blue-900 mb-1 flex items-center space-x-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Asking about selected text</span>
                  </div>
                  <pre className="text-xs text-blue-800 whitespace-pre-wrap font-mono leading-relaxed max-h-24 overflow-y-auto">
                    {contextText}
                  </pre>
                </div>
                <button
                  onClick={() => setContextText('')}
                  className="text-blue-600 hover:text-blue-800 p-1"
                >
                  <MdClose size={16} />
                </button>
              </div>
            </div>
          )}

          {isPublicView ? (
            <div className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50">
              <p className="text-sm text-gray-600">
                Sign in to ask questions about this document.
              </p>
            </div>
          ) : (
            <>
              <textarea
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                placeholder={contextText ? "What would you like to know about this selected text?" : (isStudent ? "Ask a question about this document... (You'll see your answer immediately, but it will need instructor approval to be permanent)" : "Ask a question about this document...")}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-900/20 text-sm resize-none transition-all duration-200"
                rows={2}
                onInput={(e) => {
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 96) + 'px';
                }}
              />
              <button
                onClick={handleCreateQuestion}
                disabled={isCreatingQuestion || !newQuestion.trim()}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {isCreatingQuestion ? (
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                ) : (
                  <MdAdd size={16} />
                )}
                <span>{isCreatingQuestion ? 'Creating...' : 'Ask Question'}</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveQATab('document')}
          className={`${isStudent && !hasERD ? 'w-full' : 'flex-1'} px-6 py-3 text-sm font-medium transition-colors duration-200 ${
            activeQATab === 'document'
              ? 'text-gray-900 border-b-2 border-gray-900 bg-white'
              : 'text-gray-600 hover:text-gray-900 bg-gray-50/30'
          }`}
        >
          {isStudent ? 'Questions' : 'Document Questions'}
          {(isStudent ? (qas.length + tempStudentQAs.length) : qas.length) > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
              {isStudent ? (qas.length + tempStudentQAs.length) : qas.length}
            </span>
          )}
        </button>
        {!isStudent && (
          <button
            onClick={() => setActiveQATab('additional')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors duration-200 ${
              activeQATab === 'additional'
                ? 'text-blue-900 border-b-2 border-blue-600 bg-white'
                : 'text-blue-600 hover:text-blue-900 bg-blue-50/30'
            }`}
          >
            Additional Questions
            {additionalQas.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-xs">
                {additionalQas.length}
              </span>
            )}
          </button>
        )}
      </div>

      {/* Tabbed Content Area */}
      <div className="flex-1 overflow-y-auto">
        {activeQATab === 'document' ? (
          /* Document Questions Tab */
          <div>
            {(() => {
              // For students, combine permanent and temporary Q&As
              const displayQAs = isStudent ? [...qas, ...tempStudentQAs] : qas;
              
              if (displayQAs.length === 0) {
                return (
                  <div className="flex flex-col items-center justify-center h-full text-gray-500 px-6 py-16">
                    <MdQuestionAnswer size={48} className="mb-4 text-gray-300" />
                    <h3 className="text-lg font-medium text-gray-400 mb-2">No questions yet</h3>
                    <p className="text-center text-sm">Create a question above or add from additional questions</p>
                  </div>
                );
              }
              
              return (
                <div>
                  {displayQAs.map((qa, index) => {
                    // Check if this is a temporary Q&A for students
                    const isTempQA = isStudent && tempStudentQAs.some(temp => temp.qa_id === qa.qa_id);
                    
                    return (
                  <div
                    key={`${qa.qa_id}-${index}`}
                    className={`border-b border-gray-100 transition-colors duration-200 ${
                      !qa.visible && isInstructor ? 'bg-gray-50/50' : 'hover:bg-gray-50/30'
                    }`}
                  >
                    <div className="px-6 py-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            {editingQA === qa.qa_id ? (
                              <input
                                value={editedQuestion}
                                onChange={(e) => setEditedQuestion(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base font-medium focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-900/20"
                                autoFocus
                              />
                            ) : (
                              <>
                                <button
                                  onClick={() => toggleExpanded(qa.qa_id)}
                                  className="flex-1 text-left text-base font-medium text-gray-900 hover:text-gray-700 transition-colors duration-200"
                                >
                                  {qa.rephrased_question}
                                </button>
                                <button
                                  onClick={() => toggleExpanded(qa.qa_id)}
                                  className="ml-2 p-1 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                                >
                                  {expandedQA === qa.qa_id ? (
                                    <MdExpandLess size={20} />
                                  ) : (
                                    <MdExpandMore size={20} />
                                  )}
                                </button>
                              </>
                            )}
                          </div>
                          
                          <div className="flex items-center space-x-3 mt-2">
                            {!isStudent && (
                              <span className="text-xs text-gray-500">by {qa.created_by}</span>
                            )}
                            {/* Show status indicators for instructors */}
                            {!qa.visible && isInstructor && (
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
                                Hidden from students
                              </span>
                            )}
                            {qa.visible && isInstructor && (
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                Visible to students
                              </span>
                            )}
                            {/* Show temporary status for students */}
                            {isTempQA && isStudent && (
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                Your question (temporary)
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center space-x-1">
                          {/* Visibility toggle for instructors */}
                          {isInstructor && editingQA !== qa.qa_id && (
                            <button
                              onClick={() => handleToggleVisibility(qa.qa_id, qa.visible)}
                              className={`p-2 rounded-lg transition-colors duration-200 ${
                                qa.visible 
                                  ? 'text-green-600 hover:bg-green-100' 
                                  : 'text-gray-400 hover:bg-gray-100'
                              }`}
                              title={qa.visible ? 'Hide from students' : 'Show to students'}
                            >
                              {qa.visible ? <MdVisibility size={18} /> : <MdVisibilityOff size={18} />}
                            </button>
                          )}
                          
                          {/* Edit controls for editing state */}
                          {editingQA === qa.qa_id ? (
                            <>
                              <button
                                onClick={() => handleSaveEdit(qa.qa_id)}
                                className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors duration-200"
                              >
                                <MdSave size={18} />
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                              >
                                <MdCancel size={18} />
                              </button>
                            </>
                          ) : (
                            <>
                              {/* Instructor controls */}
                              {isInstructor && (
                                <div className="relative">
                                  <button
                                    onClick={() => setShowActionsFor(showActionsFor === qa.qa_id ? null : qa.qa_id)}
                                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                                  >
                                    <MdMoreVert size={18} />
                                  </button>
                                  
                                  {/* Action Dropdown */}
                                  {showActionsFor === qa.qa_id && (
                                    <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-gray-200/60 rounded-xl shadow-xl z-10 py-2">
                                      <button
                                        onClick={() => handleEditQA(qa)}
                                        className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                                      >
                                        <MdEdit size={16} />
                                        <span>Edit Q&A</span>
                                      </button>
                                      <div className="border-t border-gray-100 my-1"></div>
                                      <button
                                        onClick={() => handleDeleteClick(qa.qa_id)}
                                        className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200"
                                      >
                                        <MdDelete size={16} />
                                        <span>Delete</span>
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {/* Answer Section (Expanded) */}
                      {expandedQA === qa.qa_id && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <div className="bg-gray-50/60 rounded-lg p-4">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Answer</h4>
                            {editingQA === qa.qa_id ? (
                              <textarea
                                value={editedAnswer}
                                onChange={(e) => setEditedAnswer(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-900/20 resize-none"
                                rows={4}
                              />
                            ) : (
                              <div className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none">
                                <ReactMarkdown
                                  remarkPlugins={[remarkMath]}
                                  rehypePlugins={[rehypeKatex]}
                                  components={{
                                    code: ({ inline, className, children, ...props }) => {
                                      // Check if this is a runnable code block (e.g., ```matlab:runnable)
                                      const isRunnable = className && className.includes(':runnable');

                                      if (!inline && isRunnable) {
                                        // Create a stable key based on code content
                                        const codeString = typeof children === 'string' 
                                          ? children 
                                          : (children?.props?.children || '');
                                        const snippetKey = `snippet-${codeString.slice(0, 50).replace(/\s/g, '-')}-${codeString.length}`;
                                        
                                        // Render interactive code snippet
                                        return (
                                          <CodeSnippet
                                            className={className}
                                            isStudent={isStudent}
                                            snippetId={snippetKey}
                                            documentId={currentDocument?.document_id || currentDocument?.id}
                                            classContext={getClassContext()}
                                          >
                                            {children}
                                          </CodeSnippet>
                                        );
                                      }

                                      // Regular code block rendering
                                      return !inline ? (
                                        <pre className="bg-gray-100 border border-gray-200 p-4 rounded-lg mt-4 mb-4 overflow-x-auto">
                                          <code className="text-sm font-mono whitespace-pre" {...props}>
                                            {children}
                                          </code>
                                        </pre>
                                      ) : (
                                        <code
                                          className="bg-gray-100 border border-gray-200 px-2 py-1 rounded text-sm font-mono"
                                          {...props}
                                        >
                                          {children}
                                        </code>
                                      )
                                    }
                                  }}
                                >
                                  {qa.answer}
                                </ReactMarkdown>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                      );
                    })}
                </div>
              );
            })()}
          </div>
        ) : activeQATab === 'additional' ? (
          /* Additional Questions Tab */
          <div>
            {additionalLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : additionalQas.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500 px-6 py-16">
                <MdQuestionAnswer size={48} className="mb-4 text-blue-300" />
                <h3 className="text-lg font-medium text-gray-400 mb-2">No additional questions</h3>
                <p className="text-center text-sm">Ask questions about this document to see them appear here</p>
              </div>
            ) : (
              <div>
                {additionalQas.map((qa) => (
                  <div
                    key={qa.qa_id}
                    className="border-b border-gray-100 hover:bg-gray-50/30 transition-colors duration-200"
                  >
                    <div className="px-6 py-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <button
                              onClick={() => toggleExpandedAdditional(qa.qa_id)}
                              className="flex items-center space-x-2 text-left text-base font-medium text-gray-900 hover:text-gray-700 transition-colors duration-200"
                            >
                              <span>{qa.rephrased_question || qa.question}</span>
                              {expandedAdditionalQA === qa.qa_id ? (
                                <MdExpandLess size={20} className="text-gray-400" />
                              ) : (
                                <MdExpandMore size={20} className="text-gray-400" />
                              )}
                            </button>
                          </div>
                          
                          <div className="flex items-center space-x-2 text-xs text-gray-500 mb-2">
                            {!isStudent && (
                              <>
                                <span>Asked by {qa.user.firstname} {qa.user.lastname}</span>
                                <span>•</span>
                              </>
                            )}
                            <span>{new Date(qa.meta.created_at).toLocaleDateString()}</span>
                            {isInstructor && (
                              <>
                                <span>•</span>
                                <button
                                  onClick={() => handlePromoteQuestion(qa.qa_id)}
                                  className="text-blue-600 hover:text-blue-800 underline"
                                >
                                  Promote to document
                                </button>
                              </>
                            )}
                          </div>

                          {expandedAdditionalQA === qa.qa_id && (
                            <div className="mt-4 pt-4 border-t border-gray-100">
                              <div className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none">
                                <ReactMarkdown
                                  remarkPlugins={[remarkMath]}
                                  rehypePlugins={[rehypeKatex]}
                                  components={{
                                    code: ({ inline, className, children, ...props }) => {
                                      // Check if this is a runnable code block (e.g., ```matlab:runnable)
                                      const isRunnable = className && className.includes(':runnable');

                                      if (!inline && isRunnable) {
                                        // Create a stable key based on code content
                                        const codeString = typeof children === 'string' 
                                          ? children 
                                          : (children?.props?.children || '');
                                        const snippetKey = `snippet-${codeString.slice(0, 50).replace(/\s/g, '-')}-${codeString.length}`;
                                        
                                        // Render interactive code snippet
                                        return (
                                          <CodeSnippet
                                            className={className}
                                            isStudent={isStudent}
                                            snippetId={snippetKey}
                                            documentId={currentDocument?.document_id || currentDocument?.id}
                                            classContext={getClassContext()}
                                          >
                                            {children}
                                          </CodeSnippet>
                                        );
                                      }

                                      // Regular code block rendering
                                      return !inline ? (
                                        <pre className="bg-gray-100 border border-gray-200 p-4 rounded-lg mt-4 mb-4 overflow-x-auto">
                                          <code className="text-sm font-mono whitespace-pre" {...props}>
                                            {children}
                                          </code>
                                        </pre>
                                      ) : (
                                        <code
                                          className="bg-gray-100 border border-gray-200 px-2 py-1 rounded text-sm font-mono"
                                          {...props}
                                        >
                                          {children}
                                        </code>
                                      )
                                    }
                                  }}
                                >
                                  {qa.answer}
                                </ReactMarkdown>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </div>
      </>)}

      {/* Delete Confirmation Modal */}
      {deleteConfirmQA && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-2xl">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Question</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this question and its answer? This action cannot be undone.
            </p>
            <div className="flex space-x-3 justify-end">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteQA(deleteConfirmQA)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors duration-200"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QAPanel;